import type { Component } from 'solid-js'
import type { DivProps } from '../ui/utils-ui'
import { makePersisted } from '@solid-primitives/storage'
import { useNavigate, useParams, useSearchParams } from '@solidjs/router'
import { Logger } from 'besonders-logger'
import { createMemo, createSignal, Show, untrack } from 'solid-js'
import { AdvancedIdentitySetup } from '../components/settings/AdvancedIdentitySetup'
import { BasicIdentitySetup } from '../components/settings/BasicIdentitySetup'
import { TabsViaConfig } from '../components/TabsViaConfig'
import { DEMO_USERNAME, useAgent } from '../data/agent/AgentState'
import { ensureHomeBlockForAgent } from '../data/block-utils'
import { useAppSettings } from '../ui/app-settings'
import { useRawThread, withThread } from '../ui/reactive'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

export const [skipUserSetup, setSkipUserSetup] = makePersisted(
	createSignal(false),
	{ name: 'note3.skipUserSetup' },
)

export function shouldShowAgentSetup() {
	const { username } = useAgent()
	const result = !skipUserSetup() && username === DEMO_USERNAME
	DEBUG('recalculated memo for showUserSetup', result, { username, skip: skipUserSetup() })
	return result
}

export const AgentSetupPage: Component<DivProps & {}> = (restProps) => {
	const [searchParams] = useSearchParams()
	const routeParams = useParams()
	const navigate = useNavigate()
	LOG(`<AgentSetupPage> `, { searchParams: untrack(() => ({ ...searchParams })) })

	const appSettings = useAppSettings()
	const rawThread = useRawThread()
	const agent = useAgent()

	// Ensure the (possibly newly set up) agent has a home block, then go where the user came from.
	const finishAndRedirect = () => {
		withThread(rawThread, () => ensureHomeBlockForAgent(appSettings, rawThread, agent))
		const redirect = Array.isArray(searchParams.redirect) ? searchParams.redirect[0] : searchParams.redirect
		navigate(redirect || '/')
	}

	const skipAndRedirect = () => {
		setSkipUserSetup(true)
		finishAndRedirect()
	}

	// The Basic tab renders its own Skip/Save footer row; other tabs use the shared page-level Skip below.
	const activeTab = createMemo(() => routeParams.section?.split('/')[0] || localStorage.getItem('note3.agentSetup.activeTab') || 'basic')

	const tabConfig = createMemo(() => [
		{ panel: 'basic', label: 'Basic', component: BasicIdentitySetup, props: { onComplete: finishAndRedirect, onSkip: skipAndRedirect } },
		{ panel: 'advanced', label: 'Advanced', component: AdvancedIdentitySetup },
	])

	return (
		<div {...restProps}>
			<wa-card class='card-header w-full' style={{ '--border-colly': '#ffffff77', '--padding': 'var(--wa-spacing-medium)' }}>
				<TabsViaConfig
					config={tabConfig()}
					useRouting={true}
					routePrefix='setup'
					localStorageKey='note3.agentSetup.activeTab'
				/>
				<Show when={activeTab() !== 'basic'}>
					<div flex='~ justify-end' mt-4>
						<wa-button appearance='plain' size='s' onClick={skipAndRedirect}>
							Skip for now
						</wa-button>
					</div>
				</Show>
			</wa-card>
		</div>
	)
}
